import { useEffect, useState, useRef } from 'react'

import Styles from './LoadingAnimation.module.scss'
import AutopilotRegistrationAndOnboardingStyles from 'screens/AutopilotRegistration/AutopilotRegistrationAndOnboarding.module.scss'

import { LoadingShimmerer } from 'components/LoadingShimmerer/LoadingShimmerer'
import { Loading } from 'screens/VirtualMarketerOnboarding/Loading/Loading'

import { useProgress } from 'hooks/useProgress'
import { useCreateScreenshot } from 'hooks/queries/useCreateScreenshot'
import { ArrowRotateLeft } from 'blaze-ui/icons/24'

const SCREENSHOT_TIMEOUT = 30000

interface Props {
  handleBack: () => void
  websiteUrl: string
  cancelOnError?: boolean
  header?: string
  transparentBackground?: boolean
  showSupportButton?: boolean
}

export const LoadingAnimation = ({
  handleBack,
  websiteUrl,
  cancelOnError = true,
  header,
  transparentBackground = false,
  showSupportButton = false,
}: Props) => {
  const { mutateAsync: createScreenshot, isLoading: isMutating } = useCreateScreenshot()
  const [screenshotUrl, setScreenshotUrl] = useState<string | null>(null)
  const timeoutRef = useRef<NodeJS.Timeout | null>(null)

  const {
    start: startThree,
    isRunning: isRunningThree,
    progress: progressThree,
  } = useProgress({ linearProgress: true, speed: 35 })
  const {
    start: startTwo,
    isRunning: isRunningTwo,
    progress: progressTwo,
  } = useProgress({ linearProgress: true, speed: 35, immediateComplete: true, onDone: startThree })
  const {
    start: startOne,
    isRunning: isRunningOne,
    progress: progressOne,
  } = useProgress({ linearProgress: true, speed: 75, immediateComplete: true, onDone: startTwo })

  if (!isRunningOne && progressOne === 0) {
    startOne()
  }

  useEffect(() => {
    if (screenshotUrl || isMutating) {
      return
    }

    const fetchScreenshot = async () => {
      if (websiteUrl) {
        timeoutRef.current = setTimeout(() => {
          if (cancelOnError) handleBack()
        }, SCREENSHOT_TIMEOUT)

        try {
          const result = await createScreenshot({
            url: websiteUrl,
          })

          if (result) {
            setScreenshotUrl(result)
          }
        } catch (error: unknown) {
          if (cancelOnError) handleBack()
        } finally {
          if (timeoutRef.current) {
            clearTimeout(timeoutRef.current)
            timeoutRef.current = null
          }
        }
      }
    }

    fetchScreenshot()

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
        timeoutRef.current = null
      }
    }
  }, [createScreenshot, screenshotUrl, isMutating, websiteUrl, handleBack, cancelOnError])

  return (
    <Loading
      backgroundClassName={AutopilotRegistrationAndOnboardingStyles.cloudBackground}
      backButtonText="Retry"
      backButtonIcon={ArrowRotateLeft}
      showSupportButton={showSupportButton}
      onCancel={handleBack}
      header={header}
      progresses={[
        {
          title: 'Analyzing your website',
          isRunning: isRunningOne,
          percentComplete: progressOne,
        },
        {
          title: 'Locating business',
          isRunning: isRunningTwo,
          percentComplete: progressTwo,
        },
        {
          title: 'Looking up competitors',
          isRunning: isRunningThree,
          percentComplete: progressThree,
        },
      ]}
    >
      <div className={Styles.imageContainer}>
        {screenshotUrl ? (
          <img src={screenshotUrl} alt="Initial Screenshot" />
        ) : (
          <LoadingShimmerer>
            <div className={Styles.screenshotPlaceholder} />
          </LoadingShimmerer>
        )}
      </div>
    </Loading>
  )
}
